import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Trash2,
  Copy,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Ruler,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useAnnotations } from '@/hooks/useAnnotations';
import { ColorPicker } from '@/components/ui/color-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Context type for annotations - 'floor_plan' uses floor_plan_annotations table, 'elevation' uses elevation_items table
type AnnotationContext = 'floor_plan' | 'elevation';

interface AnnotationContextualToolbarProps {
  floorPlanId: string;
  selectedAnnotations: Set<string>;
  context?: AnnotationContext;
}

const STROKE_WIDTHS = [1, 2, 3, 4, 6, 8];
const FONT_SIZES = [6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

export function AnnotationContextualToolbar({
  floorPlanId,
  selectedAnnotations,
  context = 'floor_plan',
}: AnnotationContextualToolbarProps) {
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const toggleEntitySelection = useAppStore(state => state.toggleEntitySelection);
  const { annotations, deleteAnnotation, createAnnotation, updateAnnotation } = useAnnotations(
    floorPlanId,
    context,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle delete
  const performDelete = useCallback(async () => {
    const annotationIds = Array.from(selectedAnnotations);

    // Clear selection immediately for better UX
    clearEntitySelection('annotation');

    // Delete all annotations in parallel
    await Promise.all(annotationIds.map(id => deleteAnnotation(id, { showSuccessToast: false })));
  }, [selectedAnnotations, deleteAnnotation, clearEntitySelection]);

  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  // Handle duplicate
  const handleDuplicate = useCallback(async () => {
    const annotationIds = Array.from(selectedAnnotations);
    const newAnnotationIds: string[] = [];

    for (const id of annotationIds) {
      const annotation = annotations.find(a => a.id === id);
      if (!annotation) continue;

      // Create a copy with slight offset.
      // Top-level shallow copy is fine for primitive position fields, but for
      // line/arrow/measurement we MUST replace start/end with NEW objects --
      // otherwise the duplicate shares start/end refs with the original and
      // dragging one moves both.
      const offset = 20;
      const newData = { ...annotation.data };

      // Adjust position based on type
      switch (annotation.type) {
        case 'rectangle':
        case 'image':
        case 'text':
          newData.x = newData.x + offset;
          newData.y = newData.y + offset;
          break;

        case 'circle':
          newData.cx = newData.cx + offset;
          newData.cy = newData.cy + offset;
          break;

        case 'line':
        case 'arrow':
        case 'measurement':
          newData.start = {
            x: newData.start.x + offset,
            y: newData.start.y + offset,
          };
          newData.end = {
            x: newData.end.x + offset,
            y: newData.end.y + offset,
          };
          break;
      }

      // Pass the entity id under the right field name so the hook's optimistic
      // update and downstream insert use the correct table without falling
      // through to a default branch.
      const createPayload =
        context === 'elevation'
          ? {
              elevation_id: floorPlanId,
              type: annotation.type,
              data: newData,
              style: annotation.style,
            }
          : {
              floor_plan_id: floorPlanId,
              type: annotation.type,
              data: newData,
              style: annotation.style,
            };

      const newAnnotation = await createAnnotation(createPayload as any);
      if (newAnnotation?.id) {
        newAnnotationIds.push(newAnnotation.id);
      }
    }

    // Replace the selection with the newly-duplicated annotations so their
    // handles and the contextual toolbar follow the user's eye.
    if (newAnnotationIds.length > 0) {
      clearEntitySelection('annotation');
      for (const id of newAnnotationIds) {
        toggleEntitySelection('annotation', id);
      }
    }
  }, [
    selectedAnnotations,
    annotations,
    createAnnotation,
    clearEntitySelection,
    toggleEntitySelection,
    floorPlanId,
    context,
  ]);

  // Get selected annotations data
  const selectedAnnotationData = useMemo(() => {
    return Array.from(selectedAnnotations)
      .map(id => annotations.find(a => a.id === id))
      .filter(Boolean);
  }, [selectedAnnotations, annotations]);

  // Determine what controls to show based on selected annotation types
  const controlVisibility = useMemo(() => {
    const types = new Set(selectedAnnotationData.map(a => a?.type));
    const hasShapes = types.has('rectangle') || types.has('circle');
    const hasLines = types.has('line') || types.has('arrow');
    const hasText = types.has('text');
    const hasMeasurement = types.has('measurement');
    const hasImage = types.has('image');
    // Dimension-toggleable types: rectangle, circle, line, arrow.
    // (Measurement always shows its value, text/image have no inherent dimension.)
    const dimensionableTypes = ['rectangle', 'circle', 'line', 'arrow'];
    const allAreDimensionable =
      selectedAnnotationData.length > 0 &&
      selectedAnnotationData.every(a => a && dimensionableTypes.includes(a.type));

    return {
      strokeColor: hasShapes || hasLines || hasText || hasMeasurement,
      fillColor: hasShapes,
      strokeWidth: hasShapes || hasLines || hasMeasurement,
      // Font size applies to text AND measurement (the value label).
      fontSize: hasText || hasMeasurement,
      fontWeight: hasText,
      textAlign: hasText,
      showAnyStyleControls: hasShapes || hasLines || hasText || hasMeasurement,
      showDimensionsToggle: allAreDimensionable,
    };
  }, [selectedAnnotationData]);

  // Aggregate showDimensions state — "on" if at least one selected annotation has it set.
  // Toggling the button applies the inverse to ALL selected annotations.
  const dimensionsToggleOn = useMemo(
    () => selectedAnnotationData.some(a => a?.data?.showDimensions),
    [selectedAnnotationData],
  );

  const handleToggleDimensions = useCallback(async () => {
    const next = !dimensionsToggleOn;
    const annotationIds = Array.from(selectedAnnotations);
    await Promise.all(
      annotationIds.map(id => {
        const annotation = annotations.find(a => a.id === id);
        if (!annotation) return Promise.resolve();
        return updateAnnotation({
          id,
          data: {
            data: {
              ...annotation.data,
              showDimensions: next,
            },
          },
        });
      }),
    );
  }, [dimensionsToggleOn, selectedAnnotations, annotations, updateAnnotation]);

  // Get current style values (use first selected annotation as reference)
  const currentStyle = useMemo(() => {
    const first = selectedAnnotationData[0];
    if (!first)
      return {
        color: '#000000',
        fill: 'none',
        strokeWidth: 2,
        fontSize: 14,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
      };

    return {
      color: first.style?.color || '#000000',
      fill: first.style?.fill || 'none',
      strokeWidth: first.style?.strokeWidth || 2,
      fontSize: first.style?.fontSize || 14,
      fontWeight: first.style?.fontWeight || 'normal',
      fontStyle: first.style?.fontStyle || 'normal',
      textAlign: first.style?.textAlign || 'left',
    };
  }, [selectedAnnotationData]);

  // Style update handlers - merge with existing style to prevent overwrites
  const handleStyleChange = useCallback(
    async (styleUpdates: Record<string, any>) => {
      const annotationIds = Array.from(selectedAnnotations);

      await Promise.all(
        annotationIds.map(id => {
          const annotation = annotations.find(a => a.id === id);
          if (!annotation) return Promise.resolve();

          // Merge existing style with updates to prevent losing other properties
          const mergedStyle = {
            ...annotation.style,
            ...styleUpdates,
          };

          return updateAnnotation({
            id,
            data: { style: mergedStyle },
          });
        }),
      );
    },
    [selectedAnnotations, annotations, updateAnnotation],
  );

  const handleColorChange = useCallback(
    (color: string) => {
      handleStyleChange({ color });
    },
    [handleStyleChange],
  );

  const handleFillChange = useCallback(
    (fill: string) => {
      handleStyleChange({ fill: fill || 'none' });
    },
    [handleStyleChange],
  );

  const handleStrokeWidthChange = useCallback(
    (width: string) => {
      handleStyleChange({ strokeWidth: parseInt(width, 10) });
    },
    [handleStyleChange],
  );

  const handleFontSizeChange = useCallback(
    (size: string) => {
      handleStyleChange({ fontSize: parseInt(size, 10) });
    },
    [handleStyleChange],
  );

  const handleToggleBold = useCallback(() => {
    const newWeight = currentStyle.fontWeight === 'bold' ? 'normal' : 'bold';
    handleStyleChange({ fontWeight: newWeight });
  }, [currentStyle.fontWeight, handleStyleChange]);

  const handleToggleItalic = useCallback(() => {
    const newStyle = currentStyle.fontStyle === 'italic' ? 'normal' : 'italic';
    handleStyleChange({ fontStyle: newStyle });
  }, [currentStyle.fontStyle, handleStyleChange]);

  const handleTextAlignChange = useCallback(
    (align: 'left' | 'center' | 'right') => {
      handleStyleChange({ textAlign: align });
    },
    [handleStyleChange],
  );

  if (selectedAnnotations.size === 0) return null;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <div
          className={cn(
            'absolute top-4 left-1/2 -translate-x-1/2 z-[100]',
            'bg-white/95 backdrop-blur-sm rounded-xl',
            'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)]',
            'p-1.5 flex items-center gap-1',
            'animate-in fade-in-0 slide-in-from-top-2 duration-200',
          )}
        >
          {/* Style Controls */}
          {controlVisibility.showAnyStyleControls && (
            <>
              {/* Stroke/Text Color */}
              {controlVisibility.strokeColor && (
                <div className="px-1">
                  <ColorPicker
                    color={currentStyle.color}
                    onChange={handleColorChange}
                    label={controlVisibility.fillColor ? 'Stroke' : 'Color'}
                  />
                </div>
              )}

              {/* Fill Color (shapes only) */}
              {controlVisibility.fillColor && (
                <div className="px-1">
                  <ColorPicker color={currentStyle.fill} onChange={handleFillChange} label="Fill" />
                </div>
              )}

              {/* Stroke Width */}
              {controlVisibility.strokeWidth && (
                <Select
                  value={currentStyle.strokeWidth.toString()}
                  onValueChange={handleStrokeWidthChange}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SelectTrigger className="w-[68px] h-8 text-xs border-0 bg-transparent hover:bg-gray-100 focus:ring-0 focus:ring-offset-0">
                        <SelectValue placeholder="Width" />
                      </SelectTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Stroke Width
                    </TooltipContent>
                  </Tooltip>
                  <SelectContent align="center">
                    {STROKE_WIDTHS.map(w => (
                      <SelectItem key={w} value={w.toString()} className="text-xs">
                        <div className="flex items-center gap-2">
                          <div
                            className="bg-gray-800 rounded-full"
                            style={{ width: 14, height: Math.max(w, 1) }}
                          />
                          <span>{w}px</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Divider */}
              {(controlVisibility.strokeColor ||
                controlVisibility.fillColor ||
                controlVisibility.strokeWidth) &&
                controlVisibility.fontSize && <div className="w-px h-5 bg-gray-200 mx-0.5" />}

              {/* Font Size (text only) */}
              {controlVisibility.fontSize && (
                <Select
                  value={currentStyle.fontSize.toString()}
                  onValueChange={handleFontSizeChange}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SelectTrigger className="w-[64px] h-8 text-xs border-0 bg-transparent hover:bg-gray-100 focus:ring-0 focus:ring-offset-0">
                        <SelectValue placeholder="Size" />
                      </SelectTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Font Size
                    </TooltipContent>
                  </Tooltip>
                  <SelectContent align="center">
                    {FONT_SIZES.map(s => (
                      <SelectItem key={s} value={s.toString()} className="text-xs">
                        {s}px
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Text Formatting Group (text only) */}
              {controlVisibility.fontWeight && (
                <div className="flex items-center bg-gray-50 rounded-md p-0.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleToggleBold}
                        className={cn(
                          'h-7 w-7 p-0 rounded',
                          currentStyle.fontWeight === 'bold'
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-100',
                        )}
                      >
                        <Bold className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Bold (⌘B)
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleToggleItalic}
                        className={cn(
                          'h-7 w-7 p-0 rounded',
                          currentStyle.fontStyle === 'italic'
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-100',
                        )}
                      >
                        <Italic className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Italic (⌘I)
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}

              {/* Text Alignment Group (text only) */}
              {controlVisibility.textAlign && (
                <div className="flex items-center bg-gray-50 rounded-md p-0.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTextAlignChange('left')}
                        className={cn(
                          'h-7 w-7 p-0 rounded',
                          currentStyle.textAlign === 'left'
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-100',
                        )}
                      >
                        <AlignLeft className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Align Left
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTextAlignChange('center')}
                        className={cn(
                          'h-7 w-7 p-0 rounded',
                          currentStyle.textAlign === 'center'
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-100',
                        )}
                      >
                        <AlignCenter className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Align Center
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTextAlignChange('right')}
                        className={cn(
                          'h-7 w-7 p-0 rounded',
                          currentStyle.textAlign === 'right'
                            ? 'bg-gray-200 text-gray-900'
                            : 'hover:bg-gray-100',
                        )}
                      >
                        <AlignRight className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Align Right
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}

              {/* Main Divider */}
              <div className="w-px h-5 bg-gray-200 mx-0.5" />
            </>
          )}

          {/* Action Buttons */}
          {controlVisibility.showDimensionsToggle && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleToggleDimensions}
                    className={cn(
                      'h-8 w-8 p-0 rounded-md',
                      dimensionsToggleOn
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'hover:bg-gray-100',
                    )}
                    aria-pressed={dimensionsToggleOn}
                  >
                    <Ruler className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {dimensionsToggleOn ? 'Hide Dimensions' : 'Show Dimensions'}
                </TooltipContent>
              </Tooltip>

              <div className="w-px h-5 bg-gray-200 mx-0.5" />
            </>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDuplicate}
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-md"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Duplicate (⌘D)
            </TooltipContent>
          </Tooltip>

          <div className="w-px h-5 bg-gray-200 mx-0.5" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDelete}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Delete (⌫)
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete annotations?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedAnnotations.size}{' '}
              {selectedAnnotations.size === 1 ? 'annotation' : 'annotations'}. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
